export interface LeadMagnetSubmitPayload {
  email: string;
  firstName?: string;
  source: string;
  country?: string;
  consent: true;
  idempotencyKey?: string;
  honeypot?: string;
  answers: Record<string, unknown>;
}

export interface LeadMagnetSubmitResponse {
  ok: boolean;
  leadMagnet: string;
  leadId: string;
  correlationId: string;
  result: {
    provider: string;
    payload: Record<string, unknown>;
  };
  message: string;
}

export type ExpertType = "expert_comptable" | "courtier" | "chasseur_immobilier";

export interface LeadMagnetExpertOptinPayload {
  email: string;
  firstName?: string;
  source: string;
  wantsExpert?: boolean;
  expertTypes?: ExpertType[];
}

const FALLBACK_RETRY_DELAY_MS = 2_000;
const MAX_SUBMIT_ATTEMPTS = 2;

export class ApiClient {
  private readonly baseUrl: string;

  public constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }

  public async submitLeadMagnet(
    slug: string,
    payload: LeadMagnetSubmitPayload,
    idempotencyKey?: string,
  ): Promise<LeadMagnetSubmitResponse> {
    for (let attempt = 1; attempt <= MAX_SUBMIT_ATTEMPTS; attempt += 1) {
      try {
        const response = await fetch(
          `${this.baseUrl}/v1/lead-magnets/${slug}/submit`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(idempotencyKey
                ? { "X-Idempotency-Key": idempotencyKey }
                : {}),
            },
            body: JSON.stringify(payload),
          },
        );

        const body = await readJsonBody(response);
        if (response.ok) {
          return body as unknown as LeadMagnetSubmitResponse;
        }

        const message =
          typeof body.message === "string"
            ? body.message
            : "La soumission a echoue.";
        if (
          attempt < MAX_SUBMIT_ATTEMPTS &&
          isRetryableStatus(response.status)
        ) {
          await delay(FALLBACK_RETRY_DELAY_MS);
          continue;
        }

        throw new Error(message);
      } catch (error) {
        if (attempt < MAX_SUBMIT_ATTEMPTS && isRetryableError(error)) {
          await delay(FALLBACK_RETRY_DELAY_MS);
          continue;
        }

        throw error instanceof Error
          ? error
          : new Error("La soumission a echoue.");
      }
    }

    throw new Error("La soumission a echoue.");
  }

  public async submitExpertOptin(
    slug: string,
    payload: LeadMagnetExpertOptinPayload,
  ): Promise<{ ok: boolean; message: string }> {
    const response = await fetch(`${this.baseUrl}/v1/lead-magnets/${slug}/expert-optin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const body = await readJsonBody(response);
    if (response.ok) {
      return {
        ok: Boolean(body.ok),
        message:
          typeof body.message === "string"
            ? body.message
            : "Demande enregistree.",
      };
    }

    throw new Error(
      typeof body.message === "string"
        ? body.message
        : "La soumission a echoue.",
    );
  }
}

function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

function isRetryableError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return true;
  }

  return error instanceof Error && error.name === "AbortError";
}

async function readJsonBody(
  response: Response,
): Promise<Record<string, unknown>> {
  const text = await response.text();
  if (!text.trim()) {
    return {};
  }

  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
